import useTvApi, { messageOf } from "hooks/useTvApi";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  PageHeader,
  Pagination,
  Table,
  TextField,
} from "stories/Admin";
import type { Column } from "stories/Admin";

/*
 * Superadmin: hvem som har lastet ned PDF-kalendere (bønnetider for året og
 * Ramadan-kalender), for hvilken moské og hvilket år, og fra hvilken klient.
 */

const PAGE_SIZE = 50;

export interface PrayerCalendarLogItem {
  id: number;
  createdAt: string;
  kind: "YEAR" | "RAMADAN";
  orgNr: string;
  mosqueName: string | null;
  year: number;
  email: string | null;
  uid: string | null;
  platform: string | null;
}

interface LogPage {
  items: PrayerCalendarLogItem[];
  total: number;
  page: number;
  size: number;
}

const PLATFORM: Record<string, string> = {
  web: "Web",
  ios: "iOS",
  android: "Android",
};

/** «25.10.2026 16:40». */
export const formatTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const PrayerCalendarLogPage: React.FC = () => {
  const api = useTvApi();
  const [page, setPage] = useState(1);
  const [orgNr, setOrgNr] = useState("");
  const [data, setData] = useState<LogPage | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const query = new URLSearchParams({
      page: String(page - 1),
      size: String(PAGE_SIZE),
    });
    if (orgNr.trim()) query.set("orgNr", orgNr.trim());
    api<LogPage>(`/api/v1/admin/prayer-calendar-log?${query}`, "GET")
      .then((res) => {
        if (!active) return;
        setData(res);
        setError("");
      })
      .catch((e) => active && setError(messageOf(e)));
    return () => {
      active = false;
    };
  }, [api, page, orgNr]);

  const columns: Column<PrayerCalendarLogItem>[] = [
    {
      key: "time",
      header: "Tidspunkt",
      render: (r) => formatTime(r.createdAt),
    },
    {
      key: "kind",
      header: "Kalender",
      render: (r) => (
        <Badge tone={r.kind === "RAMADAN" ? "gold" : "mint"}>
          {r.kind === "RAMADAN" ? `Ramadan ${r.year}` : `Bønnetider ${r.year}`}
        </Badge>
      ),
    },
    {
      key: "mosque",
      header: "Moské",
      render: (r) => (
        <>
          <b>{r.mosqueName || r.orgNr}</b>
          <div>{r.orgNr}</div>
        </>
      ),
    },
    {
      key: "user",
      header: "Lastet ned av",
      render: (r) => r.email || r.uid || "—",
    },
    {
      key: "platform",
      header: "Fra",
      render: (r) => (r.platform ? (PLATFORM[r.platform] ?? r.platform) : "—"),
    },
  ];

  return (
    <>
      <PageHeader
        title="Kalender-logg"
        subtitle="Hvem som har lastet ned bønnetider for året eller Ramadan-kalenderen som PDF."
      />
      <Card>
        <TextField
          label="Filtrer på org.nr."
          placeholder="F.eks. 982842840"
          value={orgNr}
          onChange={(e) => {
            setOrgNr(e.target.value);
            setPage(1);
          }}
        />
        {error && <Alert tone="error">{error}</Alert>}
        <Table
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(r) => r.id}
          empty={
            <EmptyState icon="doc" title="Ingen nedlastinger ennå">
              Når en moské laster ned en PDF-kalender, vises det her.
            </EmptyState>
          }
        />
        <Pagination
          page={page}
          pageCount={Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))}
          onChange={setPage}
        />
      </Card>
    </>
  );
};

export default PrayerCalendarLogPage;
